import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from "@/shared/ui/button";

import { AlertDialogContext, AlertDialogOptions } from '@/shared/hooks/AlertDialogContext';
import { extractErrorMessage } from '@/shared/lib/apiErrorHandler';
import { useQueryClient } from '@tanstack/react-query';

export const AlertDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<AlertDialogOptions>({
    title: '',
    description: '',
    confirmText: '확인',
    cancelText: '취소',
  });
  const queryClient = useQueryClient();

  const promiseRef = useRef<{
    resolve: (value: boolean) => void;
  } | null>(null);

  const showAlert = useCallback((alertOptions: AlertDialogOptions): Promise<boolean> => {
    setOptions({
      confirmText: '확인',
      cancelText: '취소',
      preventOutsideClose: true,
      ...alertOptions,
    });
    setIsOpen(true);

    return new Promise((resolve) => {
      promiseRef.current = { resolve };
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    promiseRef.current?.resolve(true);
    promiseRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    promiseRef.current?.resolve(false);
    promiseRef.current = null;
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    // Dialog가 외부에서 닫힐 때 (ESC 키, 외부 클릭 등)
    if (!open && promiseRef.current) {
      promiseRef.current.resolve(false);
      promiseRef.current = null;
    }
  }, []);

  // React Query mutationCache를 구독하여 전역 에러 처리
  useEffect(() => {
    const processedMutations = new Set<number>();

    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      // mutationUpdated 이벤트에서만 처리
      if (event.type === 'updated') {
        const mutation = event.mutation;
        const mutationId = mutation.mutationId;

        // mutation이 실패했고, 에러가 있고, 개별 onError가 없는 경우에만 처리
        if (
          mutation.state.status === 'error' &&
          mutation.state.error &&
          !mutation.options.onError && // 개별 onError가 있으면 전역 핸들러를 건너뜀
          !processedMutations.has(mutationId) // 이미 처리한 mutation은 건너뜀
        ) {
          processedMutations.add(mutationId);
          const error = mutation.state.error;
          const errorMessage = extractErrorMessage(error);
          showAlert({
            title: '오류',
            description: errorMessage,
            confirmText: '확인',
            cancelText: undefined,
          });

          // mutation이 제거되면 Set에서도 제거 (메모리 누수 방지)
          setTimeout(() => {
            processedMutations.delete(mutationId);
          }, 1000);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient, showAlert]);

  return (
    <AlertDialogContext.Provider value={{ showAlert }}>
      {children}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          onInteractOutside={(e) => {
            if (options.preventOutsideClose) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (options.preventOutsideClose) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{options.title}</DialogTitle>
            {options.description && (
              <DialogDescription className='whitespace-pre-line text-black'>
                {options.description}
              </DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter className='flex-row gap-3 pt-2'>
            {options.cancelText && (
              <Button variant="outline" onClick={handleCancel} className='flex-1'>
                {options.cancelText}
              </Button>
            )}
            <Button
              onClick={handleConfirm}
              variant={options.confirmButtonVariant === 'destructive' ? 'destructive' : 'default'}
              className='flex-1'
            >
              {options.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AlertDialogContext.Provider>
  );
};
